import { useState } from 'react'

import type { SymbolDto } from '../../contracts/api'
import { SymbolCrest } from '../components/SymbolCrest'
import { useNav } from '../nav'
import { haptic } from '../telegram'
import { seenSymbols, useSymbols } from './symbolsData'
import './symbols.css'

/**
 * «Символы» — узкая колода: только те сущности, что есть в твоих картах.
 * Врождённое отделено от периодного намеренно: первое не меняется никогда,
 * второе уйдёт вместе с периодом, и путать их нельзя.
 *
 * `notFound` — возврат с карточки, которой нет: показываем ту же сетку, а не
 * тупик с кнопкой «назад».
 */
export function Symbols({ notFound = false }: { notFound?: boolean }) {
  const { data, error } = useSymbols()
  // Читаем один раз на монтирование: пока человек смотрит сетку, бейджи не
  // должны исчезать под рукой. Экран пересоздаётся после возврата из символа —
  // тогда метка и снимется.
  const [seen] = useState(() => seenSymbols())

  if (error) return <div className="state">{error}</div>
  if (!data) return <div className="state">Собираем колоду…</div>

  const natal = data.symbols.filter((symbol) => symbol.kind === 'natal')
  const period = data.symbols.filter((symbol) => symbol.kind === 'period')

  return (
    <div className="screen symbols">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">символы</div>
      </div>

      <h1 className="display">Символы</h1>
      <div className="sub">
        {notFound ? 'символ не найден' : 'Узкая колода: только те сущности, что есть в твоих картах.'}
      </div>

      <Section title="Врождённое" note="не меняется" symbols={natal} seen={seen} />
      <Section title="Сейчас в периоде" note="уйдёт вместе с периодом" symbols={period} seen={seen} />

      {data.symbols.length === 0 && (
        <div className="card symbols-empty">
          <p>Колода пуста: карты ещё не построены.</p>
        </div>
      )}
    </div>
  )
}

function Section({
  title,
  note,
  symbols,
  seen,
}: {
  title: string
  note: string
  symbols: SymbolDto[]
  seen: Set<string>
}) {
  if (symbols.length === 0) return null
  return (
    <>
      <div className="symbols-sect">
        <span className="lbl">{title}</span>
        <span className="symbols-sect-note">{note}</span>
      </div>
      <div className="symbols-grid">
        {symbols.map((symbol) => (
          <SymbolTile key={symbol.id} symbol={symbol} fresh={!seen.has(symbol.id)} />
        ))}
      </div>
    </>
  )
}

function SymbolTile({ symbol, fresh }: { symbol: SymbolDto; fresh: boolean }) {
  const { navigate } = useNav()
  return (
    <button
      type="button"
      className={`symcard${symbol.active ? ' active' : ''}`}
      onClick={() => {
        haptic('light')
        navigate({ screen: { kind: 'symbol', id: symbol.id } })
      }}
    >
      {fresh && <span className="badge badge-new symcard-badge">новая</span>}
      <SymbolCrest symbol={symbol} size={72} />
      <span className="symcard-title">{symbol.title}</span>
      <span className="symcard-sub">{symbol.subtitle}</span>
      {symbol.active && (
        <span className="symcard-now">
          <span className="dot" aria-hidden="true" />
          сейчас
        </span>
      )}
    </button>
  )
}
